"use client";

import { Suspense, useState, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import GoogleAuthButton from "@/components/auth/google-auth-button";
import { MoveLeft, ShieldCheck, Box, RefreshCw, Mail, Eye, EyeOff, Lock, ArrowRight, Loader2 } from "lucide-react";
import { useSearchParams } from "next/navigation";
import { useLanguage } from "@/lib/language-context";
import { createBrowserClient } from "@supabase/ssr";
import { toast } from "sonner";

function LoginContent() {
    const searchParams = useSearchParams();
    const nextParam = searchParams.get("next") || "/";
    const next = nextParam.startsWith("/") && !nextParam.startsWith("//") ? nextParam : "/";
    const { t } = useLanguage();

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const savedEmail = localStorage.getItem("rememberedEmail");
        if (savedEmail) {
            setEmail(savedEmail);
            setRememberMe(true);
        }
    }, []);

    const handleEmailLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email || !password) {
            toast.error("Please fill in all fields");
            return;
        }

        setIsLoading(true);
        try {
            const supabase = createBrowserClient(
                process.env.NEXT_PUBLIC_SUPABASE_URL!,
                process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!
            );
            const { error } = await supabase.auth.signInWithPassword({
                email,
                password,
            });

            if (error) throw error;

            if (rememberMe) {
                localStorage.setItem("rememberedEmail", email);
            } else {
                localStorage.removeItem("rememberedEmail");
            }

            toast.success("Welcome back! Login successful.");
            window.location.href = next;
        } catch (err: any) {
            toast.error(err.message || "Invalid email or password");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="w-full min-h-screen flex flex-col lg:flex-row bg-[#081220] font-sans">
            {/* Left Side - Visual & Brand (50%) - Warm Off-white / Cream matching Website Background */}
            <div className="relative w-full lg:w-1/2 flex flex-col justify-between p-8 sm:p-12 lg:p-16 overflow-hidden border-b lg:border-b-0 lg:border-r border-[#B0B3B8]/30 bg-[#fffaf6]">
                {/* Background Image with Tint Overlay */}
                <Image
                    src="/industrial-parts-warehouse.jpg"
                    alt="Industrial Parts Warehouse"
                    fill
                    priority
                    fetchPriority="high"
                    className="object-cover absolute inset-0 z-0 opacity-15 mix-blend-overlay"
                />
                {/* Soft Gradient Overlay for Light background */}
                <div className="absolute inset-0 bg-gradient-to-br from-white/95 via-[#fffaf6]/90 to-[#fffaf6]/85 z-0"></div>
                <div className="absolute inset-0 bg-[linear-gradient(to_right,#00000003_1px,transparent_1px),linear-gradient(to_bottom,#00000003_1px,transparent_1px)] bg-[size:32px_32px] z-0"></div>

                {/* Logo Area (Original Colors/Dark on light background) */}
                <div className="relative z-10">
                    <Link href="/" className="inline-block transition-transform hover:scale-102 duration-300">
                        <Image
                            src="/file.svg"
                            alt="Unispare Logo"
                            width={440}
                            height={140}
                            className="h-28 w-auto object-contain"
                            priority
                        />
                    </Link>
                </div>

                {/* Hero Title & Features */}
                <div className="relative z-10 max-w-xl my-8 lg:my-auto">
                    <h2 className="text-2xl sm:text-3xl lg:text-[34px] font-extrabold text-[#1A3263] mb-6 leading-[1.2] tracking-tight">
                        Powering Industrial Excellence Across the Globe
                    </h2>
                    <p className="text-[#4B4E54] text-sm sm:text-base mb-8 leading-relaxed font-normal">
                        Access thousands of premium spare parts, track orders in real-time, and keep your operations running smoothly with our trusted supply chain network.
                    </p>

                    {/* Feature Items */}
                    <div className="space-y-4">
                        <div className="flex items-start gap-3">
                            <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-[#1A3263]/10 border border-[#1A3263]/20 flex items-center justify-center text-[#1A3263]">
                                <ShieldCheck className="w-4.5 h-4.5" />
                            </div>
                            <div>
                                <h3 className="font-semibold text-[#1A3263] text-sm sm:text-[15px]">Verified Suppliers & Quality</h3>
                                <p className="text-xs sm:text-[13px] text-[#4B4E54] mt-0.5">100% genuine industrial components sourced directly.</p>
                            </div>
                        </div>

                        <div className="flex items-start gap-3">
                            <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-[#1A3263]/10 border border-[#1A3263]/20 flex items-center justify-center text-[#1A3263]">
                                <Box className="w-4.5 h-4.5" />
                            </div>
                            <div>
                                <h3 className="font-semibold text-[#1A3263] text-sm sm:text-[15px]">Fast Global Shipping</h3>
                                <p className="text-xs sm:text-[13px] text-[#4B4E54] mt-0.5">DHL, FedEx and UPS delivery options worldwide.</p>
                            </div>
                        </div>

                        <div className="flex items-start gap-3">
                            <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-[#1A3263]/10 border border-[#1A3263]/20 flex items-center justify-center text-[#1A3263]">
                                <RefreshCw className="w-4 h-4" />
                            </div>
                            <div>
                                <h3 className="font-semibold text-[#1A3263] text-sm sm:text-[15px]">Real-time Order Tracking</h3>
                                <p className="text-xs sm:text-[13px] text-[#4B4E54] mt-0.5">Track quotes, shipments and re-orders in one click.</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Testimonial Quote */}
                <div className="relative z-10 mt-6 bg-white/60 backdrop-blur-md border border-slate-200/80 p-6 rounded-2xl max-w-lg shadow-sm">
                    <blockquote className="space-y-4">
                        <p className="text-sm sm:text-base text-slate-700 italic leading-relaxed">
                            &ldquo;Unispare has completely transformed how we source components. The reliability and speed are unmatched in the sector.&rdquo;
                        </p>
                        <footer className="flex items-center gap-3">
                            <div>
                                <div className="text-sm font-semibold text-[#1A3263]">Anant Singh</div>
                                <div className="text-xs text-slate-500">Procurement Director</div>
                            </div>
                        </footer>
                    </blockquote>
                </div>
            </div>

            {/* Right Side - Form Panel (50%) - Primary Website Dark Navy Theme */}
            <div className="w-full lg:w-1/2 flex flex-col justify-center items-center p-6 sm:p-12 lg:p-16 bg-gradient-to-br from-[#0F172A] via-[#1A3263] to-[#0F172A] relative">
                {/* Back to store Button */}
                <Link
                    href="/"
                    className="lg:absolute lg:top-12 lg:right-16 inline-flex items-center text-lg font-semibold text-slate-400 hover:text-white transition-all mb-8 lg:mb-0 bg-slate-900/60 hover:bg-slate-900 px-8 py-4 rounded-2xl border border-slate-800 hover:border-slate-700 hover:translate-y-[-1px] shadow-md"
                >
                    <MoveLeft className="w-8 h-8 mr-3" />
                    Back to store
                </Link>

                {/* Form Card Container - Styled like a premium clean white card */}
                <div className="w-full max-w-[440px] space-y-8 bg-white border border-slate-200 p-6 sm:p-10 rounded-2xl shadow-2xl">
                    <div className="text-center sm:text-left">
                        <h2 className="text-2xl sm:text-[28px] font-bold tracking-tight text-[#1A3263] leading-none">
                            Welcome Back
                        </h2>
                        <p className="mt-3 text-sm sm:text-base text-slate-550">
                            Enter your credentials to access your account
                        </p>
                    </div>

                    {/* Google OAuth Login Button */}
                    <div className="space-y-6">
                        <GoogleAuthButton nextUrl={next} />

                        {/* OR Divider */}
                        <div className="relative">
                            <div className="absolute inset-0 flex items-center">
                                <span className="w-full border-t border-slate-150" />
                            </div>
                            <div className="relative flex justify-center text-xs uppercase">
                                <span className="bg-white px-3 text-slate-600 font-semibold tracking-wider">
                                    OR
                                </span>
                            </div>
                        </div>

                        {/* Email/Password Login Form */}
                        <form onSubmit={handleEmailLogin} className="space-y-4">
                            {/* Email Field */}
                            <div className="space-y-2">
                                <label htmlFor="email" className="block text-sm font-semibold text-[#4B4E54]">
                                    Email address
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                                        <Mail className="h-4.5 w-4.5" />
                                    </div>
                                    <input
                                        id="email"
                                        type="email"
                                        required
                                        placeholder="name@company.com"
                                        className="block w-full pl-10 pr-3 py-2.5 bg-white border border-slate-200 hover:border-slate-350 focus:border-[#1A3263] rounded-xl outline-none text-sm text-slate-900 placeholder:text-slate-405 focus:ring-3 focus:ring-[#1A3263]/5 transition-all"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        disabled={isLoading}
                                    />
                                </div>
                            </div>

                            {/* Password Field */}
                            <div className="space-y-2">
                                <div className="flex justify-between items-center">
                                    <label htmlFor="password" className="block text-sm font-semibold text-[#4B4E54]">
                                        Password
                                    </label>
                                </div>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                                        <Lock className="h-4.5 w-4.5" />
                                    </div>
                                    <input
                                        id="password"
                                        type={showPassword ? "text" : "password"}
                                        required
                                        placeholder="Enter your password"
                                        className="block w-full pl-10 pr-10 py-2.5 bg-white border border-slate-200 hover:border-slate-350 focus:border-[#1A3263] rounded-xl outline-none text-sm text-slate-900 placeholder:text-slate-405 focus:ring-3 focus:ring-[#1A3263]/5 transition-all"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        disabled={isLoading}
                                    />
                                    <button
                                        type="button"
                                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-655 transition-colors"
                                        onClick={() => setShowPassword(!showPassword)}
                                        aria-label={showPassword ? "Hide password" : "Show password"}
                                    >
                                        {showPassword ? <EyeOff className="h-4.5 w-4.5" /> : <Eye className="h-4.5 w-4.5" />}
                                    </button>
                                </div>
                            </div>

                            {/* Remember me & Forgot Password */}
                            <div className="flex items-center justify-between pt-1 text-sm">
                                <label className="flex items-center gap-2 text-slate-600 cursor-pointer select-none">
                                    <input
                                        type="checkbox"
                                        checked={rememberMe}
                                        onChange={(e) => setRememberMe(e.target.checked)}
                                        className="w-4.5 h-4.5 rounded border-slate-300 bg-white checked:bg-[#1A3263] checked:border-transparent outline-none focus:ring-0"
                                    />
                                    Remember me
                                </label>
                                <Link
                                    href="/forgot-password"
                                    className="font-semibold text-[#1A3263] hover:text-[#4682B4] hover:underline transition-all"
                                >
                                    Forgot password?
                                </Link>
                            </div>

                            {/* Submit Button */}
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="w-full mt-2 h-11 bg-gradient-to-r from-[#1A3263] to-[#4682B4] hover:from-[#122448] hover:to-[#366890] text-white font-semibold rounded-xl shadow-sm hover:shadow-md transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-75 disabled:cursor-not-allowed hover:translate-y-[-1px] active:translate-y-[0px]"
                            >
                                {isLoading ? (
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                ) : (
                                    <>
                                        Sign in to your account
                                        <ArrowRight className="w-4.5 h-4.5" />
                                    </>
                                )}
                            </button>
                        </form>

                        {/* Sign up Link */}
                        <div className="text-center text-sm pt-2">
                            <span className="text-slate-500">Don&apos;t have an account? </span>
                            <Link
                                href={`/signup?next=${encodeURIComponent(next)}`}
                                className="font-semibold text-[#1A3263] hover:text-[#4682B4] hover:underline transition-all"
                            >
                                Create an account
                            </Link>
                        </div>
                    </div>

                    {/* Legal Footer Links */}
                    <p className="text-center text-xs text-slate-600 leading-relaxed border-t border-slate-100 pt-6">
                        By continuing, you agree to our{" "}
                        <Link
                            href="/terms"
                            className="underline underline-offset-4 text-slate-700 hover:text-[#1A3263] transition-colors"
                        >
                            Terms of Service
                        </Link>{" "}
                        and{" "}
                        <Link
                            href="/privacy"
                            className="underline underline-offset-4 text-slate-700 hover:text-[#1A3263] transition-colors"
                        >
                            Privacy Policy
                        </Link>
                        .
                    </p>
                </div>
            </div>
        </div>
    );
}

export default function LoginClient() {
    return (
        <Suspense fallback={
            <div className="flex h-screen w-full items-center justify-center bg-[#081220]">
                <div className="text-slate-455 animate-pulse font-medium">Loading secure login...</div>
            </div>
        }>
            <LoginContent />
        </Suspense>
    );
}
